import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { DecorativeFlourish } from './DecorativeElements';
import AnimatedSection from './AnimatedSection';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Load YouTube IFrame API
    if (typeof window === 'undefined' || !window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    }

    // Initialize player when API is ready
    const initializePlayer = () => {
      if (window.YT && window.YT.Player && containerRef.current) {
        try {
          playerRef.current = new window.YT.Player('youtube-player', {
            videoId: 'yxfWnkBvduo',
            playerVars: {
              autoplay: 1,
              mute: 1,
              loop: 1,
              controls: 0,
              showinfo: 0,
              rel: 0,
              iv_load_policy: 3,
              playlist: 'yxfWnkBvduo', // Required for loop to work
            },
            events: {
              onReady: (event) => {
                event.target.playVideo();
              },
            },
          });
        } catch (error) {
          console.warn('YouTube player initialization error:', error);
        }
      }
    };

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initializePlayer();
    } else {
      // Wait for API to load
      window.onYouTubeIframeAPIReady = initializePlayer;
    }

    // Cleanup
    return () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.warn('Error destroying YouTube player:', e);
        }
      }
      if (window.onYouTubeIframeAPIReady === initializePlayer) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* YouTube Video Background */}
      <div
        ref={containerRef}
        className="absolute inset-0 w-full h-full"
        style={{
          paddingBottom: '56.25%', // 16:9 aspect ratio
          height: '100%',
          paddingBottom: 0,
        }}
      >
        <div
          id="youtube-player"
          className="absolute top-0 left-0 w-full h-full"
          style={{
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 warm-lighting" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
        <AnimatedSection animation="fadeInUp">
          <DecorativeFlourish className="mb-4" />
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-rustic-cream mb-2 tracking-wide">
            {t.hero.welcome}
          </h2>
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif text-rustic-cream mb-4 tracking-wide">
            {t.hero.title}
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl font-body text-rustic-cream/90 mb-8 italic">
            {t.hero.subtitle}
          </p>
          <a
            href="#about"
            className="btn-primary inline-block shadow-embossed-lg hover:shadow-embossed transition-all duration-300"
          >
            {t.hero.explore}
          </a>
          <DecorativeFlourish className="mt-6" />
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Hero;
